import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { rentalApi } from "@/api/rentalApi";
import { useUIStore } from "@/store/uiStore";
import type { RentalPropertyFilter, RentalRequestFilter } from "@/types";
import { getApiErrorMessage } from "@/utils";

export function useRentalProperties(filters: RentalPropertyFilter) {
  return useQuery({
    queryKey: ["rental-properties", "list", filters],
    queryFn: () => rentalApi.getProperties(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 3,
  });
}

export function useGuestRentalProperties(filters: RentalPropertyFilter) {
  return useQuery({
    queryKey: ["guest-rental-properties", "list", filters],
    queryFn: () => rentalApi.getGuestProperties(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 3,
  });
}

export function useRentalProperty(id: string) {
  return useQuery({
    queryKey: ["rental-properties", id],
    queryFn: () => rentalApi.getPropertyById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useRentalRequests(filters: RentalRequestFilter) {
  return useQuery({
    queryKey: ["rental-requests", "list", filters],
    queryFn: () => rentalApi.getRentalRequests(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 3,
  });
}

export function useGuestRentalRequests(filters: RentalRequestFilter) {
  return useQuery({
    queryKey: ["guest-rental-requests", "list", filters],
    queryFn: () => rentalApi.getGuestRentalRequests(filters),
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60 * 3,
  });
}

export function useRentalRequest(id: string) {
  return useQuery({
    queryKey: ["rental-requests", id],
    queryFn: () => rentalApi.getRentalRequestById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateRentalRequest() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: any) => rentalApi.createRentalRequest(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rental-requests"] });
      addToast("success", "Đăng nhu cầu thuê thành công!");
    },
    onError: (err: any) => addToast("error", getApiErrorMessage(err, "Đăng nhu cầu thất bại")),
  });
}

export function useUpdateRentalRequest() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => rentalApi.updateRentalRequest(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["rental-requests", "list"] });
      qc.invalidateQueries({ queryKey: ["rental-requests", id] });
      addToast("success", "Cập nhật nhu cầu thành công!");
    },
    onError: (err: any) => addToast("error", getApiErrorMessage(err, "Cập nhật nhu cầu thất bại")),
  });
}

export function useCreateRentalProperty() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: any) => rentalApi.createProperty(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rental-properties"] });
      addToast("success", "Đăng tin cho thuê thành công!");
    },
    onError: (err: any) => addToast("error", getApiErrorMessage(err, "Đăng tin thất bại")),
  });
}

export function useUpdateRentalProperty() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => rentalApi.updateProperty(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["rental-properties", "list"] });
      qc.invalidateQueries({ queryKey: ["rental-properties", id] });
      addToast("success", "Cập nhật tin cho thuê thành công!");
    },
    onError: (err: any) => addToast("error", getApiErrorMessage(err, "Cập nhật tin thất bại")),
  });
}
